import React from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import logo from "../../../../assets/access-lg-logo.png";
import { isDarkColor } from "../../../../components/utils/themeDetector";
import usePrivileges from "../../../../hooks/usePrivileges";

// ─────────────────────────────────────────────────────────────────────────────
// MENU CONFIGURATION — driven by actual API privilege slugs
//
// Confirmed slugs from live /admin/roles API response:
//   category-management.view    module-management.view
//   course-management.view      content-management.view
//   organization-management.view learner-management.view
//   reports-management.view     role-management.view
//   analytics-management.view   notification-management.view
//   system-settings-management.view  audit-tracking.view
//
// slug: null → always visible for all users
// ─────────────────────────────────────────────────────────────────────────────
const MENU_CONFIG = [
  // ── Always visible ───────────────────────────────────────────────────────
  {
    label: "Dashboard",
    path: "/admin/dashboard",
    icon: "bi bi-grid",
    slug: null,
  },

  // ── Core LMS modules ─────────────────────────────────────────────────────
  {
    label: "Course Management",
    icon: "bi bi-journal-text",
    slug: null, // Always visible if it has visible children
    children: [
      {
        label: "Categories",
        path: "/admin/categories",
        icon: "bi bi-tags",
        slug: "category-management.view",
      },
      {
        label: "Modules",
        path: "/admin/modules",
        icon: "bi bi-box-seam",
        slug: "module-management.view",
      },
      {
        label: "Courses",
        path: "/admin/course",
        icon: "bi bi-mortarboard",
        slug: "course-management.view",
      },
    ]
  },
  {
    label: "Learning Paths",
    path: "/admin/learning-path",
    icon: "bi bi-bar-chart-steps",
    slug: "learning-path-management.view",
  },
  {
    label: "Learners",
    path: "/admin/learners",
    icon: "bi bi-people",
    slug: "learner-management.view",
  },
  {
    label: "Action Plans",
    path: "/admin/learners/action-plan",
    icon: "bi bi-journal-check",
    slug: "learner-management.view",
  },


  // ── Settings ─────────────────────────────────────────────────────────────
  {
    label: "Settings",
    path: "/admin/settings/admins",
    icon: "bi bi-gear",
    slug: "system-settings-management.view",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
const Sidebar_ = () => {
  const location = useLocation();
  const { can } = usePrivileges();

  const org = JSON.parse(sessionStorage.getItem("user"));
  const themes = sessionStorage.getItem("account-customization");

  const bgColor =
    themes
      ? JSON.parse(themes)?.theme
      : org?.orgAppearance?.themeColour || "#0A3059";

  const logoSrc =
    themes
      ? JSON.parse(themes)?.logo
      : org?.orgAppearance?.desktopLogo || logo;

  const textColor = isDarkColor(bgColor) ? "#FFFFFF !important" : "#222 !important";

  return (
    <div>
      <section>
        <div
          style={{ backgroundColor: bgColor }}
          className="text-white relative shadow"
        >
          <Sidebar
            rootStyles={{
              height: "100vh",
              opacity: 1,
              paddingRight: 10,
              paddingLeft: 10,
            }}
            width="250px"
          >
            <Menu
              menuItemStyles={{
                button: ({ active }) => ({
                  color: active ? "#FF9100 !important" : textColor,
                  backgroundColor: active ? "#FFFFFF" : "transparent",
                  borderRadius: active ? "10px" : 0,
                }),
              }}
            >
              {/* Logo */}
              <div className="border-b mb-1 border-gray-600 flex items-center pt-3 pb-2 px-3">
                <img
                  src={logoSrc}
                  alt="logo"
                  className="w-[120px] h-[50px] pl-5"
                />
              </div>

              {/* ── Render menu items from MENU_CONFIG ── */}
              {MENU_CONFIG.map((item) => {
                // Skip if user does not have this privilege
                if (!can(item.slug)) return null;

                // SubMenu with children
                if (item.children) {
                  const visibleChildren = item.children.filter((child) =>
                    can(child.slug)
                  );
                  if (!visibleChildren.length) return null;

                  return (
                    <SubMenu
                      key={item.label}
                      icon={<span className={`${item.icon} text-2xl`} />}
                      label={item.label}
                    >
                      {visibleChildren.map((child) => (
                        <MenuItem
                          key={child.label}
                          active={location.pathname.includes(child.path)}
                          component={<Link to={child.path} />}
                        >
                          <i className={child.icon} /> {child.label}
                        </MenuItem>
                      ))}
                    </SubMenu>
                  );
                }

                // Regular MenuItem
                return (
                  <MenuItem
                    key={item.label}
                    active={
                      item.path === "/admin/learners"
                        ? location.pathname.includes(item.path) && !location.pathname.includes("/action-plan")
                        : location.pathname.includes(item.path)
                    }
                    icon={<span className={`${item.icon} text-2xl`} />}
                    component={<Link to={item.path} />}
                  >
                    {item.label}
                  </MenuItem>
                );
              })}
            </Menu>
          </Sidebar>
        </div>
      </section>
    </div>
  );
};

export default Sidebar_;
